import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ItemResponseModelV2 } from './models/item-response-model-v2';
import { ItemEditRequestModel } from './models/item-edit-request-model';
import { ItemRequestModel } from './models/item-request-model';
import { ExportControl } from './models/export-control.model';
import { SupplierResponseModel } from './models/supplier-response-model';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from "@progress/kendo-data-query";

@Injectable({
  providedIn: 'root',
})
export class ItemAPIService extends BehaviorSubject<GridDataResult>  {
  public baseUrlLocal: string = environment.apiBaseUrl;
  public baseUrlLocalV2: string = environment.apiBaseUrlv2;
  public loading: boolean = false;
  public readonly queryError$ = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient) {
    super({ data: [], total: null });     
  }

  public query(state: State, filter: CompositeFilterDescriptor): void {
    this.queryError$.next(null);
    this.loading = true;
    this.fetch(state, filter).pipe(
      finalize(() => (this.loading = false))
    ).subscribe({
      next: (result) => super.next(result),
      error: () => this.queryError$.next('Failed to load items. Please try again.')
    });
  }

  protected fetch(state: State, filter: CompositeFilterDescriptor) : Observable<GridDataResult> {
    let queryStart = '';
    let queryString = '';

    queryStart = this.getQueryStart(queryStart);
    queryString = queryString.concat(`${queryStart}pageindex=${state.skip}`)
    queryStart = this.getQueryStart(queryStart);
    queryString = queryString.concat(`${queryStart}pagesize=${state.take}`)
    
    const itemIdFilter = this.getItemStateFilterValue(filter, 'itemId');
    if(itemIdFilter != undefined && itemIdFilter.length >= 3) {
        queryString = queryString.concat(`${queryStart}itemId=${encodeURIComponent(itemIdFilter)}`)
    }

    const descriptionFilter = this.getItemStateFilterValue(filter, 'description');
    if(descriptionFilter != undefined && descriptionFilter.length >= 3) {
        queryString = queryString.concat(`${queryStart}description=${encodeURIComponent(descriptionFilter)}`)
    }

    const detailedDescriptionFilter = this.getItemStateFilterValue(filter, 'detailedDescription');
    if(detailedDescriptionFilter != undefined && detailedDescriptionFilter.length >= 3) {
        queryString = queryString.concat(`${queryStart}detailedDescription=${encodeURIComponent(detailedDescriptionFilter)}`)
    }

    const itemDataResponsibleFilter = this.getItemStateFilterValue(filter, 'itemDataResponsible');
    if(itemDataResponsibleFilter != undefined && itemDataResponsibleFilter.length >= 3) {
        queryString = queryString.concat(`${queryStart}itemDataResponsible=${encodeURIComponent(itemDataResponsibleFilter)}`)
    }

    const itemStateFilter = this.getItemStateFilterValue(filter, 'itemState');
    if(itemStateFilter != undefined && itemStateFilter.length > 0) {
        queryString = queryString.concat(`${queryStart}itemState=${itemStateFilter}`)
    }

    const originSourceFilter = this.getItemStateFilterValue(filter, 'originSource');
    if(originSourceFilter != undefined && originSourceFilter.length >= 3) {
        queryString = queryString.concat(`${queryStart}originSource=${encodeURIComponent(originSourceFilter)}`)
    }

    const publishingSourceFilter = this.getItemStateFilterValue(filter, 'publishingSource');
    if(publishingSourceFilter != undefined && publishingSourceFilter.length >= 3) {
        queryString = queryString.concat(`${queryStart}publishingSource=${encodeURIComponent(publishingSourceFilter)}`)
    }

    const isReplacedFilter = this.getItemStateFilterValue(filter, 'isReplaced');
    if(isReplacedFilter != undefined && isReplacedFilter.length > 0) {
        queryString = queryString.concat(`${queryStart}isReplaced=${isReplacedFilter}`)
    }

    return this.http.get<GridDataResult>(this.baseUrlLocal + `items/search` + queryString)
      .pipe(
        map(
          (response: GridDataResult) => {
              return {
                  data: response.data,
                  total: response.total
              };
          }
        )
      );
  }

  public getItem(itemId: string) : Observable<ItemResponseModelV2> {
    return this.http.get<ItemResponseModelV2>(this.baseUrlLocal + `items/` + encodeURIComponent(itemId) + '?includeSuppliers=true&includeDeleted=true');
  }

    public getItemv2(itemId: string) : Observable<ItemResponseModelV2> {
    return this.http.get<ItemResponseModelV2>(this.baseUrlLocalV2 + `items/` + encodeURIComponent(itemId) + '?includeSuppliers=true&includeDeleted=true');
  }

  public updateItem(itemProperty: ItemEditRequestModel): Observable<ItemEditRequestModel> {
    return this.http.put<ItemEditRequestModel>(`${this.baseUrlLocal}items`, itemProperty);
  }

  public updateItemFull(itemProperty: ItemRequestModel): Observable<ItemRequestModel> {
    return this.http.put<ItemRequestModel>(`${this.baseUrlLocal}items/full`, itemProperty);
  }

  public createItem(item: ItemRequestModel): Observable<ItemRequestModel> {
    return this.http.post<ItemRequestModel>(`${this.baseUrlLocal}items`, item);
  }

  public resendItems(itemIds: string[]): Observable<any> {
    const body = {
      items: itemIds.map(id => ({ itemId: id }))
    };
    return this.http.post<any>(`${this.baseUrlLocal}items/resend`, body);
  }

  public getSyncItemMaster(itemId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrlLocal}items/syncitemmaster?itemId=${itemId}`).pipe(
      map(response => {
        // If response is a string, parse it to remove escape characters
        if (typeof response === 'string') {
          return JSON.parse(response);
        }
        return response;
      })
    );
  }

  public getSuppliers(itemId: string): Observable<SupplierResponseModel[]> {
    return this.http.get<GridDataResult>(`${this.baseUrlLocal}suppliers/${itemId}`).pipe(
      map(response => response.data as SupplierResponseModel[])
    );
  }

  // Fetch export control data
  public getExportControls(itemId: string): Observable<ExportControl[]> {
    return this.http.get<ExportControl[]>(`${this.baseUrlLocal}ExportControls/${encodeURIComponent(itemId)}`);
  }

  private getQueryStart(queryStart: string) {
    if(queryStart === '') {
      return  '?';  
    } else {
      return '&';
    }
  }

  private getItemStateFilterValue(filter: CompositeFilterDescriptor, field: string): string | undefined {
    if (!filter?.filters?.length) {
        return undefined;
    }

    const matchingFilter = filter.filters.find((item): item is FilterDescriptor => {
        return !('filters' in item) && item.field === field;
    });

    if (matchingFilter?.value === undefined || matchingFilter.value === null) {
        return undefined;
    }

    return String(matchingFilter.value);
  }
}

@Injectable()
export class ItemsService extends ItemAPIService {
    constructor(http: HttpClient) {
        super(http);
    }
}
